import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { AgentApprovalsService } from '../../agent/agent-approvals.service';
import { AgentVoiceService } from '../../agent/agent-voice.service';
import { AgentApprovalAction, AgentChatService, AgentThread, AgentTrace } from '../../agent/agent-chat.service';
import {
  AgentApprovalField,
  approvalFieldLabel,
  approvalFieldValue,
  approvalFieldsDescribed,
} from '../../agent/approval-fields';

export interface AgentThreadGroup {
  key: 'today' | 'week' | 'older';
  threads: AgentThread[];
}

/** Questions offered in an empty conversation (i18n `agentPanel.starters.*`). */
export const AGENT_STARTERS = ['explainPage', 'whatHere', 'myAgents', 'whereModel', 'myKnowledge'] as const;

const DAY_MS = 24 * 60 * 60 * 1000;
const MOBILE_MAX = 767;
/** Space between the floating panel and the header / window edges (matches right/bottom in the SCSS). */
const PANEL_GAP = 12;
/** Header height of the dashboard layout when none can be measured. */
const HEADER_FALLBACK = 76;

/** Today / last 7 days / older, newest first inside each group. */
export function groupThreads(threads: AgentThread[], now = new Date()): AgentThreadGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const groups: Record<AgentThreadGroup['key'], AgentThread[]> = { today: [], week: [], older: [] };
  for (const thread of threads) {
    const at = Date.parse(thread.updated_at ?? thread.created_at ?? '') || 0;
    const key = at >= startOfToday ? 'today' : at >= startOfToday - 6 * DAY_MS ? 'week' : 'older';
    groups[key].push(thread);
  }
  return (['today', 'week', 'older'] as const)
    .map((key) => ({ key, threads: groups[key] }))
    .filter((g) => g.threads.length > 0);
}

/**
 * The in-house agent's panel — slides in on the right of every dashboard page.
 *
 * Mounted in `nb-layout-column` (not as a sibling: nb-layout only projects
 * nb-sidebar / nb-layout-column / header / footer) with `position: fixed`
 * below the header; the column makes room via a transparent `border-right` (dashboard
 * layout). Width animates via `width`, never `transform` (rule 12). State
 * lives in AgentChatService so a conversation survives the admin/normal
 * layout switch. ⌘K / Ctrl+K toggles it.
 */
@Component({
  selector: 'app-agent-panel',
  templateUrl: './agent-panel.component.html',
  styleUrls: ['./agent-panel.component.scss'],
})
export class AgentPanelComponent implements AfterViewInit, OnDestroy {
  readonly chat = inject(AgentChatService);
  readonly approvals = inject(AgentApprovalsService);
  readonly voice = inject(AgentVoiceService);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly toastr = inject(NbToastrService);

  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;

  readonly starters = AGENT_STARTERS;
  readonly groups = computed(() => groupThreads(this.chat.threads()));
  readonly title = computed(() => {
    const id = this.chat.threadId();
    return this.chat.threads().find((t) => t.id === id)?.title ?? null;
  });

  draft = '';
  historyOpen = false;
  top = HEADER_FALLBACK + PANEL_GAP;
  mobile = false;
  private resizing = false;
  private headerObserver?: ResizeObserver;
  private observedHeader?: Element;

  constructor() {
    // Changes the person's own agent proposed over MCP — polled while the agent is on.
    effect(() => this.approvals.watch(this.chat.enabled(), this.chat.open()), { allowSignalWrites: true });
    // Follow the conversation: keep the newest message in view.
    effect(() => {
      this.chat.messages();
      queueMicrotask(() => {
        const el = this.scroller?.nativeElement;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });
    effect(() => {
      if (this.chat.open()) {
        // The layout may have switched (admin/normal) since the last measure.
        this.measure();
        queueMicrotask(() => this.input?.nativeElement.focus());
      } else {
        this.voice.stop();
        this.stopDictation();
      }
    }, { allowSignalWrites: true });
    // Voice mode: a question that was spoken gets its answer read aloud.
    effect(() => {
      const messages = this.chat.messages();
      if (!this.voiceReply || this.chat.busy()) return;
      const last = messages[messages.length - 1];
      if (last?.role === 'agent' && !last.streaming && !last.error && last.text) {
        this.voiceReply = false;
        this.voice.play(last.id, last.text);
      }
    }, { allowSignalWrites: true });
  }

  ngAfterViewInit(): void {
    this.measure();
  }

  ngOnDestroy(): void {
    this.headerObserver?.disconnect();
    this.stopDictation();
    this.voice.stop();
  }

  /**
   * The panel floats as its own window below the header: `top` = the bottom of
   * the header the user actually sees + a gap. The dashboard layout is mounted
   * twice (normal and admin), so `querySelector` alone could pick a header that
   * is not rendered — its bottom of 0 put the panel over the header. The header
   * is watched, so a late layout or a height change moves the panel too.
   */
  @HostListener('window:resize')
  measure(): void {
    const header = Array.from(document.querySelectorAll('nb-layout-header')).find(
      (el) => el.getBoundingClientRect().height > 0
    );
    const bottom = header ? header.getBoundingClientRect().bottom : 0;
    this.top = Math.round((bottom > 0 ? bottom : HEADER_FALLBACK) + PANEL_GAP);
    this.mobile = window.innerWidth <= MOBILE_MAX;
    this.chat.setWidth(this.chat.width());
    this.watchHeader(header);
  }

  private watchHeader(header: Element | undefined): void {
    if (!header || header === this.observedHeader || typeof ResizeObserver === 'undefined') return;
    this.headerObserver?.disconnect();
    this.observedHeader = header;
    this.headerObserver = new ResizeObserver(() => {
      const bottom = header.getBoundingClientRect().bottom;
      if (bottom > 0) this.zone.run(() => (this.top = Math.round(bottom + PANEL_GAP)));
    });
    this.headerObserver.observe(header);
  }

  @HostListener('document:keydown', ['$event'])
  onShortcut(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.chat.toggle();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    } else if (event.key === 'Escape') {
      this.chat.close();
    }
  }

  send(text?: string): void {
    const value = (text ?? this.draft).trim();
    if (!value || this.chat.busy()) return;
    this.stopDictation();
    this.voice.stop();
    // Spoken question → spoken answer (the effect in the constructor plays it).
    this.voiceReply = text === undefined && this.dictated;
    this.dictated = false;
    this.draft = '';
    this.historyOpen = false;
    this.chat.send(value);
    queueMicrotask(() => this.autosize());
  }

  /** The field grows with its text (one line at rest) up to COMPOSER_MAX_PX, then scrolls. */
  autosize(): void {
    const el = this.input?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + border, COMPOSER_MAX_PX)}px`;
    el.style.overflowY = el.scrollHeight + border > COMPOSER_MAX_PX ? 'auto' : 'hidden';
  }

  // ---- dictation: the browser's speech recognition (Web Speech API), as in
  // the chat widget's text input — no backend, no credits. Chrome, Edge and
  // Safari; the button is hidden where the browser has none (Firefox). The
  // text lands in the field and is sent only on the person's click/Enter.
  readonly speechSupported = !!speechRecognitionCtor();
  listening = false;
  private recognition: SpeechRecognitionLike | null = null;
  /** the draft holds dictated words → read the answer aloud */
  private dictated = false;
  private voiceReply = false;

  toggleDictation(): void {
    if (this.listening) {
      this.stopDictation();
      return;
    }
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return;
    const recognition: SpeechRecognitionLike = new Ctor();
    recognition.lang = (this.translate.currentLang || this.translate.defaultLang || 'de').startsWith('en') ? 'en-US' : 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;
    // What was already typed stays; the dictation is appended after it.
    const base = this.draft.trim() ? `${this.draft.trimEnd()} ` : '';
    recognition.onresult = (event) =>
      this.zone.run(() => {
        let spoken = '';
        for (let i = 0; i < event.results.length; i++) spoken += event.results[i][0].transcript;
        this.draft = base + spoken.trimStart();
        this.dictated = this.dictated || !!spoken.trim();
        queueMicrotask(() => this.autosize());
      });
    recognition.onerror = (event) =>
      this.zone.run(() => {
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          this.toastr.warning(this.translate.instant('agentPanel.dictateDenied'), '');
        }
      });
    recognition.onend = () =>
      this.zone.run(() => {
        this.listening = false;
        this.recognition = null;
        this.input?.nativeElement.focus();
      });
    this.recognition = recognition;
    this.listening = true;
    recognition.start();
  }

  private stopDictation(): void {
    this.recognition?.stop();
  }

  starter(key: string): void {
    this.send(this.translate.instant(`agentPanel.starters.${key}`));
  }

  newChat(): void {
    this.historyOpen = false;
    this.chat.newChat();
    queueMicrotask(() => this.input?.nativeElement.focus());
  }

  pick(thread: AgentThread): void {
    this.historyOpen = false;
    this.chat.selectThread(thread.id);
  }

  remove(thread: AgentThread, event: Event): void {
    event.stopPropagation();
    this.chat.deleteThread(thread.id);
  }

  startResize(event: PointerEvent): void {
    if (this.mobile) return;
    this.resizing = true;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  @HostListener('document:pointermove', ['$event'])
  onResize(event: PointerEvent): void {
    if (this.resizing) this.chat.setWidth(window.innerWidth - event.clientX - 12);
  }

  @HostListener('document:pointerup')
  endResize(): void {
    if (!this.resizing) return;
    this.resizing = false;
    this.chat.setWidth(this.chat.width(), true);
  }

  /** i18n label of a capability (`agentPanel.actions.<action_id with _>`), else the spec's summary. */
  actionLabel(action: Pick<AgentApprovalAction, 'action_id' | 'summary'>): string {
    if (action.action_id) {
      const key = `agentPanel.actions.${action.action_id.replace(/\./g, '_')}`;
      const label = this.translate.instant(key);
      if (label !== key) return label;
    }
    return action.summary || this.translate.instant('agentPanel.approval.unnamed');
  }

  /** A line of the approval card in the customer's words — never `settings.<variable_name>` (bug #187). */
  fieldLabel(f: AgentApprovalField): string {
    return approvalFieldLabel(this.translate, f);
  }

  fieldValue(f: AgentApprovalField): string {
    return approvalFieldValue(this.translate, f);
  }

  fieldsDescribed(fields: AgentApprovalField[] | null | undefined): boolean {
    return approvalFieldsDescribed(fields);
  }

  traceLabel(trace: AgentTrace): string {
    const label = this.actionLabel({ action_id: trace.actionId, summary: trace.summary });
    if (trace.ok) return label;
    const outcome = trace.reason === 'rejected_by_user' ? 'agentPanel.trace.rejected' : 'agentPanel.trace.failed';
    return `${label} — ${this.translate.instant(outcome)}`;
  }

  trackThread(_: number, thread: AgentThread): string {
    return thread.id;
  }
}

/** Height up to which the composer grows before it scrolls (12rem). */
const COMPOSER_MAX_PX = 192;

/** The part of the Web Speech API the panel uses (not in TypeScript's DOM lib everywhere). */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

function speechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as Record<string, unknown>;
  return ((w['SpeechRecognition'] || w['webkitSpeechRecognition']) as new () => SpeechRecognitionLike) || null;
}

